import { memo } from "react";
import { Box, Typography } from "@mui/material";
import {
  Location as LocationType,
  SpecialLocation,
  SelectedLocationForMove,
} from "../../../../models";
import {
  groupBilletsForDisplay,
  getIndividualBilletCounts,
} from "../../../../utils/centralizedBilletGrouping";
import { HeatCommentIcon } from "../../../shared";

interface LocationSectionProps {
  location: SelectedLocationForMove | null;
  fallbackLocation?: LocationType | SpecialLocation | null;
  getLocationDisplayText: (location: LocationType | SpecialLocation) => string;
  showEmptyState?: boolean;
  onGroupSelect?: (globalIndex: number) => void;
  warningMessage?: string;
}

export const LocationSection = memo(
  ({
    location,
    fallbackLocation,
    getLocationDisplayText,
    showEmptyState = true,
    onGroupSelect,
    warningMessage,
  }: LocationSectionProps) => {
    // Test the individual billet counts function
    const individualCounts = location
      ? getIndividualBilletCounts(location.groups, location.location)
      : {};

    // Determine if we should show empty state
    const shouldShowEmptyState =
      showEmptyState && (!location || location.groups.length === 0);

    // Empty state component
    const EmptyState = () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "200px",
          textAlign: "center",
          padding: "20px",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: (theme) => theme.palette.grey[500],
            fontSize: "14px",
            fontWeight: 500,
            lineHeight: 1.5,
          }}
        >
          No billets available in this location
        </Typography>
      </Box>
    );

    // Location content with groups
    const LocationContent = () => (
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {location && location.groups.length > 0
            ? Object.entries(
                groupBilletsForDisplay(location.groups, location.location),
              ).map(([groupName, heatsInGroup]) => (
                <Box
                  key={groupName}
                  sx={{
                    marginBottom: "8px",
                  }}
                >
                  {/* Group Header */}
                  <Box
                    sx={{
                      backgroundColor: (theme) =>
                        theme.palette.billetYard.backgrounds.lighter,
                      border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                      borderRadius: "4px 4px 0 0",
                      padding: "8px 12px",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "15px",
                        fontWeight: 600,
                        color: (theme) => theme.palette.grey[700],
                        margin: 0,
                      }}
                    >
                      {heatsInGroup[0]?.GroupName || "Unknown Group"}
                    </Typography>
                  </Box>

                  {/* Heat List */}
                  <Box
                    sx={{
                      border: (theme) => `1px solid ${theme.palette.grey[300]}`,
                      borderTop: "none",
                      borderRadius: "0 0 4px 4px",
                      overflow: "hidden",
                      transition: "all 0.3s ease-in-out",
                    }}
                  >
                    {heatsInGroup
                      .sort((a, b) => {
                        // Legacy system orders by Position DESC (higher positions first)
                        return b.Position - a.Position;
                      })
                      .map((heat, heatIndex) => {
                        const globalIndex = location.groups.findIndex(
                          (g) => g.LocationContentID === heat.LocationContentID,
                        );
                        const isSelected =
                          location.selectedGroupIndex === globalIndex;
                        const isTopOfPile = heat.IsSelected;

                        return (
                          <Box
                            key={heat.LocationContentID}
                            sx={{
                              padding: "12px 16px",
                              minHeight: "44px",
                              marginBottom: "2px",
                              transition: "all 0.2s ease",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              backgroundColor: (theme) =>
                                isSelected
                                  ? theme.palette.billetYard.selected.background
                                  : theme.palette.grey[50],
                              cursor: "pointer",
                              borderBottom: (theme) =>
                                heatIndex < heatsInGroup.length - 1
                                  ? `1px solid ${theme.palette.grey[200]}`
                                  : "none",
                            }}
                            onClick={() => {
                              if (onGroupSelect) {
                                onGroupSelect(globalIndex);
                              }
                            }}
                            onTouchStart={(e) => {
                              e.currentTarget.style.transform = "scale(0.98)";
                              e.currentTarget.style.opacity = "0.8";
                            }}
                            onTouchEnd={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.opacity = "1";
                            }}
                            onMouseDown={(e) => {
                              e.currentTarget.style.transform = "scale(0.98)";
                              e.currentTarget.style.opacity = "0.8";
                            }}
                            onMouseUp={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.opacity = "1";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "scale(1)";
                              e.currentTarget.style.opacity = "1";
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                                justifyContent: "center",
                                position: "relative",
                              }}
                            >
                              <Box
                                sx={{
                                  position: "relative",
                                  display: "inline-flex",
                                  alignItems: "center",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontSize: "15px",
                                    textAlign: "center",
                                    fontWeight: 500,
                                    color: (theme) => theme.palette.grey[700],
                                  }}
                                >
                                  {heat.HeatNumber}
                                </Typography>
                                {heat.Comments ? (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      left: "100%",
                                      top: 0,
                                      bottom: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      paddingLeft: "6px",
                                    }}
                                  >
                                    <HeatCommentIcon
                                      comment={heat.Comments}
                                      heatName={heat.HeatNumber}
                                      size="medium"
                                    />
                                  </Box>
                                ) : null}
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                color: (theme) => theme.palette.grey[600],
                              }}
                            >
                              {heat.BilletsCount}
                            </Typography>
                          </Box>
                        );
                      })}
                  </Box>

                  {/* Individual Billet Counts Display - Only show if has individual counts */}
                  {individualCounts[groupName] && (
                    <Box
                      sx={{
                        marginTop: "6px",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: "14px",
                          color: (theme) => theme.palette.text.primary,
                          fontWeight: 600,
                        }}
                      >
                        {individualCounts[groupName]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))
            : null}
        </Box>
      </Box>
    );

    return (
      <Box>
        {/* Location Name Header */}

        {/* Warning Message */}
        {warningMessage && (
          <Box
            sx={{
              marginTop: "8px",
              marginBottom: "16px",
              padding: "8px 12px",
              backgroundColor: (theme) =>
                theme.palette.billetYard.backgrounds.light,
              border: (theme) => `1px solid ${theme.palette.grey[300]}`,
              borderRadius: "4px",
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                color: (theme) => theme.palette.grey[900],
                fontWeight: 500,
              }}
            >
              {warningMessage}
            </Typography>
          </Box>
        )}

        {/* Location Content */}
        {shouldShowEmptyState ? <EmptyState /> : <LocationContent />}
      </Box>
    );
  },
);

LocationSection.displayName = "LocationSection";
